import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';

import './mobile-product-list-top-section-style.scss';
import useIntersectionObserver from '@src/hooks/useIntersectionObserver';

import SortListIcon from '@src/componentsNew/Icons/Icons/SortListIcon';
import FilterListIcon from '@src/componentsNew/Icons/Icons/FilterListIcon';
import SearchIcon from '@src/componentsNew/Icons/Icons/SearchIcon';
import WishlistIcon from '@src/componentsNew/Icons/Icons/WishlistIcon';
import CartQuantityIcon from '@src/componentsNew/Icons/CartQuantityIcon/CartQuantityIcon';

import Button from '@src/componentsNew/Buttons/Button';
import Typography from '@src/componentsNew/Typography/Typography';
import Carousel from '@src/componentsNew/Carousel/Carousel';
import BasicModal from '@src/componentsNew/Modals/BasicModal/BasicModal';
import MobileCustomHeader from '@src/componentsNew/Header/CustomHeader/MobileCustomHeader';
import MobileProductListSortingFilter from '@src/componentsNew/Modals/FilterModal/MobileProductListSortingFilter/MobileProductListSortingFilter';
import MobileProductListFilter from '@src/componentsNew/Modals/FilterModal/MobileProductListFilter/MobileProductListFilter';

import { wishListUrl, searchUrl, cartUrl } from '@src/app/routes';
import { ProductListBaseProps } from '@src/componentsNew/ProductListTemplate/type';

const MobileProductListTopSection = ({
  pageTitle,
  images,
  brandInfo,
  level0SubMenu,
  level1SubMenu,
  showLevel1SubmenuAllButton,
  selectedLevel0SubMenu,
  selectedLevel1SubMenu,
  handleClickLevel0SubMenu,
  handleClickLevel1SubMenu,
  productTotalCount,
  variables,
  setVariables,
}: ProductListBaseProps) => {
  const history = useHistory();

  const [isSortingFilterModalOpen, setIsSortingFilterModalOpen] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isTncModalOpen, setIsTncModalOpen] = useState(false);

  const [imageWrapperRef, isImageWrapperRefIntersecting] = useIntersectionObserver<HTMLDivElement>({
    threshold: 0,
    rootMargin: '-57px 0px 0px 0px',
  });

  const [brandImageWrapperRef, isBrandImageWrapperRefIntersecting] = useIntersectionObserver<HTMLDivElement>({
    threshold: 0,
    rootMargin: '-130px 0px 0px 0px',
  });

  const hasBrandImages = brandInfo?.images?.length > 0;
  const hasMainImages =
    (images && typeof images === 'object' && 'url' in images) || (Array.isArray(images) && images.length > 0);

  const shouldBeFixed =
    !hasBrandImages && hasMainImages ? imageWrapperRef?.current && !isImageWrapperRefIntersecting : true;
  const shouldApplyFixedBottomArea = hasBrandImages
    ? shouldBeFixed && !isBrandImageWrapperRefIntersecting
    : shouldBeFixed;

  const topFixedAreaRef = useRef<HTMLDivElement>(null);
  const [topFixedAreaHeight, setFixedAreaHeight] = useState(0);

  useEffect(() => {
    if (topFixedAreaRef.current) {
      setFixedAreaHeight(topFixedAreaRef.current.offsetHeight);
    }
  }, [selectedLevel0SubMenu]);

  const handleSortingFilterModalOpen = () => {
    setIsSortingFilterModalOpen(true);
  };

  const handleSortingFilterModalClose = () => {
    setIsSortingFilterModalOpen(false);
  };

  const handleFilterModalOpen = () => {
    setIsFilterModalOpen(true);
  };

  const handleFilterModalClose = () => {
    setIsFilterModalOpen(false);
  };

  const handleTncModalOpen = () => {
    setIsTncModalOpen(true);
  };

  const handleTncModalClose = () => {
    setIsTncModalOpen(false);
  };

  return (
    <div id="mobile-product-list-top-section-wrapper">
      <MobileCustomHeader
        type="left"
        isShowBackBtn={true}
        backBtnColor="#21242A7A"
        pageTitle={pageTitle}
        pageTitleColor={'#212121'}
        pageTitleVariant="h8"
        pageTitleComponent="h3"
        rightChild={
          <>
            <SearchIcon onClick={() => history.push(searchUrl)} />
            <WishlistIcon onClick={() => history.push(wishListUrl)} />
            <CartQuantityIcon isMobile={true} onClick={() => history.push(cartUrl)} />
          </>
        }
      />

      <div className="top-section-content-area">
        {hasMainImages && !hasBrandImages && (
          <div className="image-wrapper" ref={imageWrapperRef}>
            {typeof images === 'object' && 'url' in images ? (
              <img className="image" src={images.url} alt={images.alt ?? ''} />
            ) : (
              <Carousel
                customSetting={{
                  dots: false,
                  infinite: true,
                  centerMode: true,
                  centerPadding: '0',
                  autoplay: true,
                  autoplaySpeed: 4000,
                  speed: 2000,
                  slidesToShow: 1,
                  slidesToScroll: 1,
                  className: 'image-slider',
                }}
              >
                {images.map((item) => (
                  <img key={item.url} className="image" src={item.url} alt={item.alt ?? ''} />
                ))}
              </Carousel>
            )}
          </div>
        )}

        <div className={`fixed-area-top ${shouldBeFixed ? 'fixed' : ''}`} ref={topFixedAreaRef}>
          {level0SubMenu?.length ? (
            <div className="submenu-area level0">
              {level0SubMenu?.map((level0, idx) => (
                <Button
                  key={idx}
                  size="medium"
                  cName={`${selectedLevel0SubMenu?.id === level0?.id ? 'selected' : ''}`}
                  onClick={(e: React.MouseEvent) => handleClickLevel0SubMenu(e, level0)}
                >
                  {level0?.name}
                </Button>
              ))}
            </div>
          ) : null}

          {showLevel1SubmenuAllButton || level1SubMenu?.length ? (
            <div className="submenu-area level1">
              {showLevel1SubmenuAllButton && (
                <Button
                  size="small"
                  cName={`${!selectedLevel1SubMenu ? 'selected' : ''}`}
                  onClick={(e: React.MouseEvent) => handleClickLevel1SubMenu(e, null)}
                >
                  All
                </Button>
              )}

              {level1SubMenu?.map((level1, idx) => (
                <Button
                  key={idx}
                  size="small"
                  cName={`${selectedLevel1SubMenu?.id === level1?.id ? 'selected' : ''}`}
                  onClick={(e: React.MouseEvent) => handleClickLevel1SubMenu(e, level1)}
                >
                  {level1?.name}
                </Button>
              ))}
            </div>
          ) : null}
        </div>

        {hasBrandImages && (
          <div
            className="image-wrapper"
            ref={brandImageWrapperRef}
            style={{ marginTop: shouldBeFixed ? `${topFixedAreaHeight}px` : 'unset' }}
          >
            <Carousel
              customSetting={{
                dots: true,
                infinite: true,
                centerMode: true,
                centerPadding: '0',
                autoplay: true,
                autoplaySpeed: 4000,
                speed: 2000,
                slidesToShow: 1,
                slidesToScroll: 1,
                className: 'image-slider',
              }}
            >
              {brandInfo?.images?.map((image) => (
                <img key={image.mobileUrl} className="brand-image" src={image.mobileUrl} alt={image?.alt ?? ''} />
              ))}
            </Carousel>
            {brandInfo?.tnc ? (
              <Typography cName="tnc-btn" variant="body3" component="span" onClick={handleTncModalOpen}>
                T&Cs
              </Typography>
            ) : null}
          </div>
        )}

        <div
          className={`fixed-area-bottom ${shouldApplyFixedBottomArea ? 'fixed' : ''}`}
          style={{ top: shouldApplyFixedBottomArea ? `${topFixedAreaHeight + 57}px` : 'unset' }}
        >
          <div className="sorting-filter-area">
            <Typography variant="body2" component="span" style={{ color: '#11141D99' }}>
              {`${productTotalCount} Products`}
            </Typography>

            <div className="sorting-icon-wrapper">
              <SortListIcon onClick={handleSortingFilterModalOpen} />
              <FilterListIcon onClick={handleFilterModalOpen} />
            </div>
          </div>
        </div>
      </div>

      {shouldApplyFixedBottomArea && <div style={{ height: `${topFixedAreaHeight + 56}px` }} />}

      {isSortingFilterModalOpen && (
        <MobileProductListSortingFilter
          open={isSortingFilterModalOpen}
          onClose={handleSortingFilterModalClose}
          variables={variables}
          setVariables={setVariables}
        />
      )}

      {isFilterModalOpen && (
        <MobileProductListFilter
          open={isFilterModalOpen}
          onClose={handleFilterModalClose}
          variables={variables}
          setVariables={setVariables}
        />
      )}

      {isTncModalOpen && (
        <BasicModal
          title="T&Cs"
          titleColor="#212121"
          content={brandInfo?.tnc}
          contentColor="#666666"
          open={isTncModalOpen}
          onClose={handleTncModalClose}
          closeButtonText="close"
          showCloseIcon={false}
        />
      )}
    </div>
  );
};

export default MobileProductListTopSection;
